import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { Loader2 } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PayPal Smart Buttons for a single SKU. Wraps order creation +
// capture against our FastAPI backend. Renders nothing if the
// surrounding PayPalScriptProvider couldn't load (the catalogue
// will still show the Stripe button).
//
// `getBooking` (optional) — returns the BookingDetails object captured
// in the parent's BookingDetailsModal. We send it to `/checkout/paypal`
// so the order is persisted with full class details before the buyer
// even approves payment.
export const PayPalSkuButton = ({ sku, onProcessing, getBooking, onCompleted }) => {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="relative" data-testid={`paypal-buttons-${sku}`}>
      {busy && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-black/40 backdrop-blur-[2px]">
          <Loader2 size={18} className="animate-spin text-brand-yellow" />
        </div>
      )}
      <PayPalButtons
        fundingSource={undefined}
        style={{
          layout: "horizontal",
          color: "gold",
          shape: "pill",
          label: "paypal",
          tagline: false,
          height: 44,
        }}
        createOrder={async () => {
          setBusy(true);
          onProcessing?.(true);
          const booking = getBooking ? getBooking() : null;
          trackEvent("begin_checkout", {
            sku,
            method: "paypal",
            currency: "GBP",
            content_ids: [sku],
            content_name: sku,
            content_type: "product",
          });
          const r = await axios.post(`${API}/checkout/paypal`, {
            sku,
            quantity: 1,
            origin_url: window.location.origin,
            customer_email: booking?.email,
            booking,
          });
          if (r.data?.mocked) {
            toast.info("PayPal not configured yet — use Card.");
            throw new Error("PayPal not configured");
          }
          return r.data.order_id;
        }}
        onApprove={async (data) => {
          try {
            const r = await axios.post(`${API}/checkout/paypal/capture`, {
              order_id: data.orderID,
            });
            if (r.data?.payment_status === "paid") {
              trackEvent("purchase", {
                sku,
                method: "paypal",
                order_id: data.orderID,
                transaction_id: data.orderID,
                value: r.data.amount,
                currency: (r.data.currency || "gbp").toUpperCase(),
                content_ids: [sku],
                content_name: sku,
                content_type: "product",
              });
              toast.success("Payment received — thanks!");
              onCompleted?.();
              navigate(`/thank-you?reason=booking&order_id=${encodeURIComponent(data.orderID)}&provider=paypal`);
            } else {
              toast.error("Payment not completed. Try again or use Card.");
            }
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't capture PayPal payment.");
          } finally {
            setBusy(false);
            onProcessing?.(false);
          }
        }}
        onCancel={() => {
          setBusy(false);
          onProcessing?.(false);
          toast.info("PayPal payment cancelled.");
        }}
        onError={(err) => {
          setBusy(false);
          onProcessing?.(false);
          // eslint-disable-next-line no-console
          console.error("PayPal error", err);
          toast.error("PayPal had a problem. Try again or use Card.");
        }}
      />
    </div>
  );
};
